import writeXlsxFile from 'write-excel-file'
import {CircularProgress, Dialog, DialogContent} from "@mui/material";
import {useEffect, useState} from "react";
import {MailFragment, useExportMailsQuery} from "../../../Queries";
import {Brick, BrickMailDefinition} from "./BrickMailType";

type ExportLine = {
   mailType: string
   language: string
   subject: string
   senderName: string
   senderMail: string
   titleText: string
   configHeader: string
   speakboxTexts: string[]
   bricks: string[]
}

/**
 * Extract all text fields from a brick based on its type
 */
const extractBrickTexts = (brick: Brick): string[] => {
   const texts: string[] = []

   if ('text' in brick && brick.text) {
      // BrickText
      if (brick.value) texts.push(brick.value)
   } else if ('button' in brick && brick.button) {
      // BrickButton
      if (brick.value) texts.push(brick.value)
   } else if ('box' in brick && brick.box) {
      // BrickBox
      if (brick.value) texts.push(brick.value)
   } else if ('image' in brick && brick.image) {
      // BrickImage
      if (brick.title) texts.push(brick.title)
   } else if ('products' in brick && brick.products) {
      // BrickProducts
      if (brick.title) texts.push(brick.title)
   } else if ('status' in brick && brick.status) {
      // BrickStatus - extract text from all blocks
      if (brick.blocks) {
         for (const blockId of Object.keys(brick.blocks)) {
            const block = brick.blocks[blockId]
            if (block?.text) texts.push(block.text)
         }
      }
   } else if ('shipping' in brick && brick.shipping) {
      // BrickShipping - extract all text fields
      if (brick.qrImageTitle) texts.push(brick.qrImageTitle)
      if (brick.qrDownloadTitle) texts.push(brick.qrDownloadTitle)
      if (brick.qrDownloadButtonTitle) texts.push(brick.qrDownloadButtonTitle)
      if (brick.shippingTitle) texts.push(brick.shippingTitle)
      if (brick.shippingButtonTitle) texts.push(brick.shippingButtonTitle)
      if (brick.labelCodeTitle) texts.push(brick.labelCodeTitle)
   }

   return texts
}

export const ExportToExcel = async (data: ExportLine[]) => {
   // Group data by mail type
   const mailTypeGroups = new Map<string, ExportLine[]>();
   for (const line of data) {
      const existing = mailTypeGroups.get(line.mailType) || [];
      existing.push(line);
      mailTypeGroups.set(line.mailType, existing);
   }

   type DataCell = {
      language: string
      row: string
      value: string
   }

   const sheetNames: string[] = [];
   const sheetsData: object[][] = [];

   for (const [mailType, lines] of mailTypeGroups) {
      sheetNames.push(mailType);

      // Get unique languages for this mail type
      const languages = Array.from(new Set(lines.map(l => l.language)));

      // Get unique rows for this mail type
      const uniqueRows = new Set<string>();
      uniqueRows.add("subject");
      uniqueRows.add("senderName");
      uniqueRows.add("senderMail");
      uniqueRows.add("titleText");
      uniqueRows.add("configHeader");
      for (const line of lines) {
         for (let speakboxIndex = 1; speakboxIndex <= line.speakboxTexts.length; speakboxIndex++) {
            uniqueRows.add("speakbox" + speakboxIndex);
         }
         for (let brickIndex = 1; brickIndex <= line.bricks.length; brickIndex++) {
            uniqueRows.add("brick" + brickIndex);
         }
      }
      const rows = Array.from(uniqueRows);

      // Build cells for this mail type
      const cells: DataCell[] = [];
      for (const line of lines) {
         cells.push({ language: line.language, row: 'subject', value: line.subject });
         cells.push({ language: line.language, row: 'senderName', value: line.senderName });
         cells.push({ language: line.language, row: 'senderMail', value: line.senderMail });
         cells.push({ language: line.language, row: 'titleText', value: line.titleText });
         cells.push({ language: line.language, row: 'configHeader', value: line.configHeader });

         let speakboxIndex = 1;
         for (const speakboxText of line.speakboxTexts) {
            cells.push({
               language: line.language,
               row: "speakbox" + speakboxIndex,
               value: getTextContentOnly(speakboxText)
            });
            speakboxIndex++;
         }

         let brickIndex = 1;
         for (const brick of line.bricks) {
            cells.push({
               language: line.language,
               row: "brick" + brickIndex,
               value: getTextContentOnly(brick)
            });
            brickIndex++;
         }
      }

      // Build sheet data as array of rows
      // First row is header: KEY, language1, language2, ...
      const headerRow: object[] = [
         { type: String, value: 'KEY' },
         ...languages.map(lang => ({ type: String, value: lang }))
      ];

      const dataRows: object[][] = [headerRow];
      for (const rowId of rows) {
         const row: object[] = [
            { type: String, value: rowId },
            ...languages.map(lang => ({
               type: String,
               value: cells.find(cell => cell.row === rowId && cell.language === lang)?.value || ''
            }))
         ];
         dataRows.push(row);
      }

      sheetsData.push(dataRows as unknown as object[]);
   }

   await writeXlsxFile(sheetsData as any, {
      sheets: sheetNames,
      fileName: 'mailtexts-' + Date.now() + '.xlsx'
   });
}

type MailTextExportProps = {
   shopId: string
}

export const MailTextExport = ({shopId}: MailTextExportProps) => {
   const [open, setOpen] = useState<boolean>(true);
   const {data} = useExportMailsQuery({
      variables: {
         shopId: shopId
      }
   });

   useEffect(() => {
      if (data?.BrickMails) {
         setOpen(true);
         (async function () {
            const exportLines: ExportLine[] = []
            const brickMails = ((data.BrickMails || []) as MailFragment[])
            for (const brickMail of brickMails) {
               if (!brickMail.active) {
                  continue
               }
               for (const mail of brickMail.mails || []) {
                  const mailDef = mail!.mail as BrickMailDefinition
                  if (!mailDef) {
                     continue;
                  }
                  // Extract speakbox texts
                  const speakboxTexts: string[] = []
                  if (mailDef.speakbox) {
                     if (mailDef.speakbox.box1) speakboxTexts.push(mailDef.speakbox.box1)
                     if (mailDef.speakbox.box1desc) speakboxTexts.push(mailDef.speakbox.box1desc)
                     if (mailDef.speakbox.box2) speakboxTexts.push(mailDef.speakbox.box2)
                     if (mailDef.speakbox.box2desc) speakboxTexts.push(mailDef.speakbox.box2desc)
                  }

                  // Extract all brick texts using type-aware extraction
                  const brickTexts: string[] = []
                  for (const brick of mailDef.bricks) {
                     brickTexts.push(...extractBrickTexts(brick))
                  }

                  exportLines.push({
                     subject: mailDef.subject,
                     senderMail: mailDef.from?.mail || '',
                     senderName: mailDef.from?.name || '',
                     language: mail!.language!,
                     mailType: brickMail!.type!,
                     titleText: mailDef.title?.text || '',
                     configHeader: mailDef.config?.header || '',
                     speakboxTexts: speakboxTexts,
                     bricks: brickTexts.filter(val => !!val)
                  })
               }
            }
            await ExportToExcel(exportLines);
            setOpen(false);
         })();
      }
   }, [data])

   return <Dialog open={open}>
      <DialogContent>
         <CircularProgress size={100}/>
         Preparing data, please wait...
      </DialogContent>
   </Dialog>
}

const getTextContentOnly = (html: string) => {
   const doc = new DOMParser().parseFromString(html, 'text/html');
   const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      null
   );
   const texts = [];
   let node = walker.nextNode()
   while (node) {
      texts.push(node.textContent);
      node = walker.nextNode()
   }
   return texts.join(' ').trim().replace(/\s\s+/g, ' ')
}
